// Připojení zařízení (připomínka 30. 10.: „srozumitelněji, graficky pěkněji, záložky“) – jedno okno pro Kalendář, Kontakty
// i Poštu v telefonu a programech:
//  • Telefon a počítač – výběr zařízení (iPhone, Android, Thunderbird, Outlook) a postup krok za krokem s vyplněnými údaji,
//    QR kódem a tlačítkem pro heslo aplikace přímo v kroku;
//  • Hesla aplikací – přehled (kdy a odkud se naposledy připojilo, pozastavené), nové heslo (ukáže se jednou), zrušení;
//  • Ruční nastavení – všechny adresy, porty a uživatelská jména s tlačítkem Kopírovat.
// Aplikace otevírají okno se `service` (caldav | carddav | mail) – postup pak začne touto službou.
import { registerComponent } from '@core/views/factory';
import { core, ApiError } from '@core/api';
import { h, clear } from '@core/dom';
import { t, tk, fmtDateTime } from '@core/i18n';
import { icon } from '@core/icons';
import { qrSvg } from '@core/components/qr';
import { IwTabs } from '@core/components/misc';
import { toast, toastError } from '@core/components/toast';
import { confirm, prompt } from '@core/components/dialog';

interface Row { id: string; label: string; created_at: string; last_at: string | null; last_ip: string | null; suspended_at?: string | null; suspended_reason?: string | null; used?: string[] }
interface Mail { host: string; imap: number; smtps: number; submission: number; boxes: { email: string; imap: boolean; smtp: boolean }[] }
interface Payload { login: string; service: 'caldav' | 'carddav' | 'mail' | null; davPath: string; calendarsPath: string; addressbooksPath: string; hasCalendar: boolean; hasContacts: boolean; mail: Mail | null }
type Platform = 'ios' | 'android' | 'thunderbird' | 'outlook';
type Service = 'caldav' | 'carddav' | 'mail';

registerComponent('desktop-app-passwords', ({ desc, win }) => {
  const p = desc.payload as unknown as Payload;
  const origin = location.origin;
  const davBase = origin + p.davPath;
  const calUrl = origin + p.calendarsPath;
  const cardUrl = origin + p.addressbooksPath;
  win.setTitle(t('desktop.devices.title'), 'devices');

  const copyBtn = (text: string): HTMLElement => {
    const b = h('button', { type: 'button', class: 'btn ghost sm', title: t('desktop.apppw.copy'), 'aria-label': t('desktop.apppw.copy') }, icon('copy'));
    b.addEventListener('click', () => void navigator.clipboard?.writeText(text).then(() => toast(t('desktop.apppw.copied'), 'ok')));
    return b;
  };
  /** Hodnota s tlačítkem Kopírovat (v kroku postupu i v ručním nastavení). */
  const value = (label: string, v: string): HTMLElement => h('div', { class: 'dev-value' }, h('span', { class: 'muted' }, label), h('code', null, v), copyBtn(v));

  const services: Service[] = [];
  if (p.hasCalendar) services.push('caldav');
  if (p.hasContacts) services.push('carddav');
  if (p.mail) services.push('mail');
  if (p.service && services.includes(p.service)) services.sort((a) => (a === p.service ? -1 : 1));
  const svcIcon: Record<Service, string> = { caldav: 'calendar', carddav: 'users', mail: 'mail' };

  // ---- hesla aplikací (sdílené: záložka i tlačítko v kroku postupu)
  const list = h('div', { class: 'dev-pw-list' });
  const fresh = h('div', { class: 'dev-pw-fresh' });
  async function loadPasswords(): Promise<void> {
    try {
      const r = await core<{ items: Row[] }>('appPasswords', 'list');
      clear(list);
      if (!r.items.length) list.appendChild(h('p', { class: 'muted' }, t('desktop.apppw.none')));
      for (const row of r.items) {
        const revoke = h('button', { type: 'button', class: 'btn sm danger' }, t('desktop.apppw.revoke'));
        revoke.addEventListener('click', async () => {
          if (!(await confirm(t('desktop.apppw.confirm_revoke', { label: row.label }), { danger: true, okLabel: t('desktop.apppw.revoke') }))) return;
          await core('appPasswords', 'revoke', { id: row.id }).catch((e: ApiError) => toastError(e));
          void loadPasswords();
        });
        const state = row.suspended_at
          ? h('span', { class: 'dev-badge warn' }, icon('warning'), t('desktop.devices.pw_suspended'))
          : row.last_at ? h('span', { class: 'dev-badge ok' }, icon('check'), t('desktop.devices.pw_used')) : h('span', { class: 'dev-badge' }, t('desktop.apppw.never_used'));
        list.appendChild(h('div', { class: 'dev-pw-row' },
          h('span', { class: 'dev-pw-ic' }, icon('devices')),
          h('div', { class: 'dev-pw-info' },
            h('div', { class: 'dev-pw-name' }, h('b', null, row.label), state),
            h('div', { class: 'muted small' }, row.last_at ? t('desktop.devices.pw_last', { date: fmtDateTime(row.last_at), ip: row.last_ip ?? '—' }) : t('desktop.devices.pw_created', { date: fmtDateTime(row.created_at) })),
            row.used?.length ? h('div', { class: 'muted small' }, t('desktop.devices.pw_used_in', { list: row.used.join(', ') })) : null,
            row.suspended_at ? h('div', { class: 'small warn' }, t('desktop.apppw.suspended', { reason: row.suspended_reason ?? '' })) : null),
          revoke));
      }
    } catch (e) {
      list.replaceChildren(h('p', { class: 'muted' }, tk((e as ApiError).message, (e as ApiError).params ?? undefined)));
    }
  }
  /** Nové heslo – ukáže se jednou (na záložce Hesla i v kroku postupu, odkud bylo vytvořené). */
  async function createPassword(into: HTMLElement, label?: string): Promise<void> {
    const name = await prompt(t('desktop.apppw.label_prompt'), label ?? t('desktop.apppw.label_default'));
    if (name === null) return;
    try {
      const r = await core<{ id: string; password: string }>('appPasswords', 'create', { label: name });
      into.replaceChildren(h('div', { class: 'dev-secret' },
        h('div', null, icon('shield'), ' ', h('b', null, t('desktop.devices.pw_new', { label: name }))),
        h('div', { class: 'dev-secret-val' }, h('code', null, r.password), copyBtn(r.password)),
        h('p', { class: 'muted small' }, t('desktop.apppw.show_once'))));
      void loadPasswords();
    } catch (e) {
      toast(tk((e as ApiError).message, (e as ApiError).params ?? undefined), 'error');
    }
  }

  // ---- postup krok za krokem
  const platformLabel: Record<Platform, string> = { ios: 'iPhone / iPad', android: 'Android', thunderbird: 'Thunderbird', outlook: 'Outlook' };
  const platforms: Platform[] = ['ios', 'android', 'thunderbird', 'outlook'];
  const ua = navigator.userAgent;
  let platform: Platform = /iPhone|iPad|Macintosh.*Mobile/.test(ua) ? 'ios' : /Android/.test(ua) ? 'android' : 'thunderbird';
  const guide = h('div', { class: 'dev-guide' });
  const picker = h('div', { class: 'dev-platforms', role: 'tablist' });
  const mailBox = p.mail?.boxes.find((b) => b.imap) ?? p.mail?.boxes[0];
  const mailUser = mailBox?.email ?? t('desktop.devices.your_email'); // pošta: jméno = adresa schránky, heslo = heslo aplikace (postup 79)

  /** Kroky pro službu a zařízení: [text, hodnoty k opsání]. */
  function steps(s: Service, pl: Platform): [string, HTMLElement[]][] {
    const pw: [string, HTMLElement[]] = [t('desktop.devices.step_password'), []];
    if (s === 'mail') {
      const m = p.mail as Mail;
      const server = [value(t('desktop.devices.v_imap'), `${m.host} : ${m.imap} (SSL/TLS)`), value(t('desktop.devices.v_smtp'), `${m.host} : ${m.smtps} (SSL/TLS)`), value(t('desktop.devices.v_user'), mailUser)];
      return [
        [t('desktop.devices.mail_' + pl), []],
        [t('desktop.devices.step_mail_values'), server],
        pw,
      ];
    }
    const url = s === 'caldav' ? calUrl : cardUrl;
    if (pl === 'android') return [[t('desktop.apppw.step_android'), [value(t('desktop.apppw.base_url'), davBase), value(t('desktop.apppw.username'), p.login)]], pw];
    if (pl === 'ios') return [[t(s === 'caldav' ? 'desktop.apppw.step_ios_cal' : 'desktop.apppw.step_ios_card'), [value(t('desktop.apppw.server'), url), value(t('desktop.apppw.username'), p.login)]], pw];
    if (pl === 'thunderbird') return [[t(s === 'caldav' ? 'desktop.apppw.step_tb_cal' : 'desktop.apppw.step_tb_card'), [value(t('desktop.apppw.server'), url), value(t('desktop.apppw.username'), p.login)]], pw];
    return [[t('desktop.apppw.step_outlook'), [value(t('desktop.apppw.server'), url), value(t('desktop.apppw.username'), p.login)]], pw];
  }
  function renderGuide(): void {
    picker.replaceChildren(...platforms.map((pl) => {
      const b = h('button', { type: 'button', class: 'dev-platform' + (pl === platform ? ' active' : ''), role: 'tab', 'aria-selected': pl === platform ? 'true' : 'false' }, icon(pl === 'ios' || pl === 'android' ? 'phone' : 'monitor'), h('span', null, platformLabel[pl]));
      b.addEventListener('click', () => { platform = pl; renderGuide(); });
      return b;
    }));
    clear(guide);
    for (const s of services) {
      const slot = h('div');
      const list2 = h('ol', { class: 'dev-steps' }, steps(s, platform).map(([text, vals], i, all) => {
        const last = i === all.length - 1;
        const btn = last ? h('button', { type: 'button', class: 'btn sm primary' }, icon('plus'), t('desktop.apppw.create')) : null;
        btn?.addEventListener('click', () => void createPassword(slot, platformLabel[platform]));
        return h('li', null, h('div', { class: 'dev-step-text' }, text), vals.length ? h('div', { class: 'dev-step-values' }, vals) : null, btn, last ? slot : null);
      }));
      const qrUrl = s === 'caldav' ? calUrl : s === 'carddav' ? cardUrl : '';
      const qr = qrUrl && (platform === 'ios' || platform === 'android') ? h('div', { class: 'dev-qr', title: t('desktop.apppw.qr_hint') }) : null;
      if (qr) qr.innerHTML = qrSvg(platform === 'android' ? davBase : qrUrl, 120);
      guide.appendChild(h('section', { class: 'dev-card' },
        h('h3', null, h('span', { class: 'dev-card-ic' }, icon(svcIcon[s])), t('desktop.devices.svc_' + s)),
        h('div', { class: 'dev-card-body' }, list2, qr ? h('div', { class: 'dev-qr-wrap' }, qr, h('span', { class: 'muted small' }, t('desktop.apppw.qr_hint'))) : null),
      ));
    }
    if (!services.length) guide.appendChild(h('p', { class: 'muted' }, t('desktop.devices.no_services')));
  }

  // ---- ruční nastavení
  function manual(): HTMLElement {
    const box = h('div', { class: 'dev-manual' });
    if (p.hasCalendar) box.appendChild(h('section', { class: 'dev-card' }, h('h3', null, h('span', { class: 'dev-card-ic' }, icon('calendar')), t('desktop.devices.svc_caldav')), value(t('desktop.apppw.server'), calUrl), value(t('desktop.apppw.base_url'), davBase), value(t('desktop.apppw.username'), p.login)));
    if (p.hasContacts) box.appendChild(h('section', { class: 'dev-card' }, h('h3', null, h('span', { class: 'dev-card-ic' }, icon('users')), t('desktop.devices.svc_carddav')), value(t('desktop.apppw.server'), cardUrl), value(t('desktop.apppw.base_url'), davBase), value(t('desktop.apppw.username'), p.login)));
    if (p.mail) {
      const m = p.mail;
      box.appendChild(h('section', { class: 'dev-card' }, h('h3', null, h('span', { class: 'dev-card-ic' }, icon('mail')), t('desktop.devices.svc_mail')),
        value(t('desktop.devices.v_imap'), `${m.host} : ${m.imap} (SSL/TLS)`),
        value(t('desktop.devices.v_smtp'), `${m.host} : ${m.smtps} (SSL/TLS)`),
        m.submission ? value(t('desktop.devices.v_submission'), `${m.host} : ${m.submission} (STARTTLS)`) : null,
        h('div', { class: 'dev-boxes' }, h('div', { class: 'muted small' }, t('desktop.devices.mail_user_hint')), m.boxes.map((b) => h('div', { class: 'dev-box' }, h('code', null, b.email), copyBtn(b.email), h('span', { class: 'muted small' }, b.imap ? t('desktop.devices.box_full') : t('desktop.devices.box_smtp')))))));
    }
    box.appendChild(h('p', { class: 'muted small' }, t('desktop.apppw.password_hint')));
    return box;
  }

  const tabs = new IwTabs();
  tabs.setTabs([{ id: 'guide', label: t('desktop.devices.tab_guide') }, { id: 'passwords', label: t('desktop.devices.tab_passwords') }, { id: 'manual', label: t('desktop.devices.tab_manual') }]);
  tabs.panel('guide').append(h('p', { class: 'muted dev-intro' }, t('desktop.devices.intro')), picker, guide);
  const createBtn = h('button', { type: 'button', class: 'btn primary' }, icon('plus'), t('desktop.apppw.create'));
  createBtn.addEventListener('click', () => void createPassword(fresh));
  tabs.panel('passwords').append(h('p', { class: 'muted dev-intro' }, t('desktop.apppw.intro')), h('div', { class: 'row' }, createBtn), fresh, list);
  tabs.panel('manual').append(manual());
  renderGuide();
  void loadPasswords();
  return { el: h('div', { class: 'view dev-view' }, tabs) };
});
